import React, { useState } from 'react';
import { Zap, Sun, Thermometer, Eye, Shield, Truck, QrCode, FileText, ChevronDown } from 'lucide-react';
import { fmt } from './promoCart';
import { BACKEND_URL } from './promoCart';
import { trackPromo } from '../../lib/promoAttribution';

const GOLD = 'text-[#C9A84C]';
const chf = (v) => (Number(v) % 1 ? fmt(v) : `${Number(v)}.–`);

// Pastilles de prix par quantité (1–9 | 10–19 | dès 20)
export const TierPills = ({ offer, activeMin }) => (
  <div className="grid gap-2" style={{ gridTemplateColumns: `repeat(${Math.min(offer.tiers.length, 4)}, minmax(0, 1fr))` }} data-testid={`tier-table-${offer.slug}`}>
    {offer.tiers.map((t) => (
      <div key={t.min} className={`rounded-xl border px-2 py-2 text-center transition-colors ${t.min === activeMin ? 'border-[#C9A84C] bg-[#C9A84C]/[0.06]' : 'border-white/10'}`} data-testid={`tier-row-${offer.slug}-${t.min}`}>
        <p className="text-[11px] text-zinc-400">{t.max ? `${t.min}–${t.max} pcs` : `dès ${t.min} pcs`}</p>
        <p className={`text-base font-semibold tabular-nums ${t.min === activeMin ? GOLD : 'text-[#F2EFE8]'}`}>{chf(t.price_ht)}</p>
      </div>
    ))}
  </div>
);

const RULES = [
  [Zap, (f) => /^\d[\d\s,.]*\s*W$/i.test(f)],
  [Sun, (f) => /\blm$/i.test(f)],
  [Thermometer, (f) => /\d\s*K\b/.test(f)],
  [Eye, (f) => /UGR/i.test(f)],
  [Shield, (f) => /IP\d/i.test(f)],
];

export const splitSpecs = (features = []) => {
  const key = [];
  const used = new Set();
  RULES.forEach(([icon, test]) => {
    if (key.length >= 4) return;
    const i = features.findIndex((f, n) => !used.has(n) && test(f));
    if (i >= 0) { used.add(i); key.push({ icon, label: features[i].replace(/\s*\(CCT\)/, ' CCT') }); }
  });
  return { key, rest: features.filter((_, n) => !used.has(n)) };
};

export const KeySpecs = ({ offer }) => {
  const [open, setOpen] = useState(false);
  const { key, rest } = splitSpecs(offer.features || []);
  return (
    <div className="space-y-2">
      {key.length > 0 && (
        <div className="grid grid-cols-4 gap-2" data-testid={`offer-key-specs-${offer.slug}`}>
          {key.map(({ icon: Icon, label }) => (
            <div key={label} className="rounded-xl border border-white/10 bg-black/30 py-2 px-1 flex flex-col items-center gap-1 text-center">
              <Icon className={`w-4 h-4 ${GOLD}`} />
              <span className="text-[12px] text-[#F2EFE8] leading-tight">{label}</span>
            </div>
          ))}
        </div>
      )}
      {rest.length > 0 && (
        <div>
          <button type="button" onClick={() => setOpen(!open)} className="min-h-[36px] text-xs text-zinc-400 hover:text-white flex items-center gap-1" data-testid={`offer-features-more-${offer.slug}`}>
            Toutes les caractéristiques <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? 'rotate-180' : ''}`} />
          </button>
          {open && (
            <ul className="mt-1 space-y-1 text-[13px] text-zinc-300 list-disc pl-5" data-testid={`offer-features-${offer.slug}`}>
              {rest.map((f) => <li key={f}>{f}</li>)}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};

export const DatasheetButton = ({ offer }) => {
  const d = (offer.documents || []).find((x) => x.doc_type === 'datasheet');
  if (!d) return null;
  return (
    <a href={`${BACKEND_URL}/api/promo-docs/${d.id}`} target="_blank" rel="noopener noreferrer"
      onClick={() => trackPromo('doc_download', { offer_slug: offer.slug, doc_id: d.id })}
      className="w-full h-11 rounded-xl border border-[#C9A84C] text-[#C9A84C] font-semibold flex items-center justify-center gap-2 hover:bg-[#C9A84C]/10 transition-colors" data-testid={`offer-datasheet-${offer.slug}`}>
      <FileText className="w-4 h-4" /> Fiche technique (PDF)
    </a>
  );
};

export const TrustRow = ({ slug }) => (
  <div className="grid grid-cols-3 gap-2 pt-3 border-t border-white/[0.07]" data-testid={`offer-trust-${slug}`}>
    {[[Truck, 'Livraison gratuite'], [Shield, 'Garantie 5 ans'], [QrCode, 'Carte ou facture QR']].map(([Icon, l]) => (
      <div key={l} className="flex flex-col items-center gap-1 text-center">
        <Icon className={`w-5 h-5 ${GOLD}`} />
        <span className="text-[11px] text-zinc-400 leading-tight">{l}</span>
      </div>
    ))}
  </div>
);

export const UnitPrice = ({ offer }) => {
  const t1 = offer.tiers[0];
  return (
    <div data-testid={`offer-unit-price-${offer.slug}`}>
      <p className="flex items-baseline gap-2">
        <span className={`text-4xl font-bold tracking-tight tabular-nums ${GOLD}`} data-testid={`offer-from-price-${offer.slug}`}>CHF {chf(t1.price_ht)}</span>
        <span className="text-sm text-zinc-400">HT / pce</span>
      </p>
      <p className="text-sm text-zinc-400 tabular-nums">
        {fmt(t1.price_ttc)} TTC
        {offer.reference_price_ht && <> · au lieu de <span className="line-through" data-testid={`offer-ref-price-${offer.slug}`}>{chf(offer.reference_price_ht)} HT</span></>}
      </p>
    </div>
  );
};
